import { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { addManyPropertiesToFilter } from "store/features/filter/filterSlice";
import { RootState } from "store/rootReducer";

import {
  SHAPEFILE_FILTER_OFF,
  ShapefileBufferDisplay,
  ShapefileFilterDraft,
  ShapefileFilterMode,
  buildShapefileBoundaryFilter,
  findShapefileBoundaryPredicate,
  shapefileBoundaryProperty,
  shapefileBufferDisplayOf,
  shapefileBufferMetersOf,
  shapefileFilterModeOf
} from "components/filter/util";

interface ShapefileFilterModeArgs {
  projectId: Maybe<string>;
  shapefileId: Maybe<string>;
  shapefileName: Maybe<string>;
}

export interface ShapefileFilterModeResult {
  /** Which way the shapefile is filtering right now, "off" when it is not. */
  mode: ShapefileFilterMode;
  /** How far past the shapefile the applied filter reaches, in metres. */
  bufferMeters: number;
  /**
   * How that distance was said when it was asked for — the number and its
   * unit — when the applied predicate carries a wording that can be believed.
   * Null otherwise, and whoever is showing the buffer converts from the metres.
   */
  bufferDisplay: Maybe<ShapefileBufferDisplay>;
  /**
   * Writes a staged mode and buffer onto this shapefile's boundary predicate,
   * with the way the buffer was said beside it, in the one dispatch every
   * caller shares — mode and buffer are never written as two operations.
   * Answers whether it was written: there is nothing to write it onto while
   * the shapefile has no name, so a control that draws off its own presses
   * has to know the press did not land.
   */
  applyDraft: (draft: ShapefileFilterDraft) => boolean;
  /**
   * Identity of the predicate the three values above were read off, which
   * changes on every write to the filter. A control holding a staged draft
   * watches this to know the applied filter moved out from under it.
   */
  predicateId: Maybe<string>;
}

/**
 * The shapefile filtering toggle's state, read straight off the applied filter
 * rather than kept alongside it: the toggle owns exactly one predicate — the
 * one on this shapefile's boundary key — so removing that predicate from the
 * filter tree puts the toggle back to Off with nothing to keep in sync.
 *
 * `applyDraft` is the only way anything reaches the filter: it goes through
 * the same reducer a legend selection uses, which replaces every predicate on
 * the property, so applying a mode updates the one predicate in place and a
 * staged Off removes it. Predicates on other shapefiles are never touched, and
 * the filter re-runs off the store change like any other. Everything the band
 * does before that press — staging a mode, a buffer, a unit — is the band's
 * own state; this hook only ever sees the one write that lands.
 *
 * The buffer rides that same predicate and is read back the same way, so it
 * survives a mode switch and a saved filter without being kept anywhere else.
 */
export function useShapefileFilterMode({
  projectId,
  shapefileId,
  shapefileName
}: ShapefileFilterModeArgs): ShapefileFilterModeResult {
  const dispatch = useDispatch();
  const propertiesFilter = useSelector(
    (state: RootState) => state.filter.propertiesFilter
  );

  const property = useMemo(
    () =>
      projectId && shapefileId ? shapefileBoundaryProperty(projectId, shapefileId) : null,
    [projectId, shapefileId]
  );

  const predicate = useMemo(
    () => (property ? findShapefileBoundaryPredicate(propertiesFilter, property) : null),
    [propertiesFilter, property]
  );

  const mode = shapefileFilterModeOf(predicate);
  const bufferMeters = shapefileBufferMetersOf(predicate);
  const bufferDisplay = useMemo(() => shapefileBufferDisplayOf(predicate), [predicate]);

  // Writes the one predicate this shapefile owns, or clears it, and answers
  // whether it wrote.
  const apply = useCallback(
    (
      nextMode: ShapefileFilterMode,
      nextBuffer: number,
      nextSaid: Maybe<ShapefileBufferDisplay>
    ): boolean => {
      // The name is what the predicate matches on, so a shapefile that has
      // none — or nothing but whitespace, which trims away to none — has
      // nothing to filter on and is left alone rather than filtering on "".
      const name = shapefileName?.trim();
      if (!property || !name) {
        return false;
      }
      const boundaryFilter =
        nextMode === SHAPEFILE_FILTER_OFF
          ? null
          : buildShapefileBoundaryFilter(property, name, nextMode, nextBuffer, nextSaid);
      dispatch(
        addManyPropertiesToFilter({
          legendFilters: boundaryFilter ? [boundaryFilter] : [],
          propertyName: property
        })
      );
      return true;
    },
    [dispatch, property, shapefileName]
  );

  // The only caller of apply(): one draft, one write. The guards that used to
  // live here against a buffer-only write over an exclusion or over Off are
  // gone with the setter they belonged to — a draft always carries a mode,
  // and a mode replacing an exclusion is the documented, intended behaviour
  // (D-05).
  const applyDraft = useCallback(
    (draft: ShapefileFilterDraft): boolean =>
      apply(draft.mode, draft.bufferMetres, draft.bufferDisplay),
    [apply]
  );

  return {
    mode,
    bufferMeters,
    bufferDisplay,
    applyDraft,
    predicateId: predicate?.id
  };
}

export default useShapefileFilterMode;
